import styles from './ui-menu-link.css';
import { Component } from '../../../decorators/component.decorator';
import { Attribute } from '../../../decorators/attribute.decorator';
import { Child } from '../../../decorators/child.decorator';
import { UIElement } from '../../ui-element';
import { Digest } from '../../../global/index';
import { insertElements } from '../../../global/render-api';
import { dispatchMenuClick } from '../menu-click.helpers';

/**
 * @class
 * @memberof SharedComponents
 * @augments {UIElement}
 * @alias UIMenuLink
 * @element ui-menu-link
 * @classdesc Represents a class for <code>ui-menu-link</code> element. Should be used inside <code>ui-menu-group</code> element.
 * @property {boolean} [current=false] {@attr current} Whether the user is on the page corresponding to this menu link.
 * @property {string} [href] {@attr href} For external links, the URL that the menu link points to
 * @property {HTMLLinkElement} link {@attr link} Read-only. Shortcut to child link element.
 * @fires event:menu-click Dispatched when the link anchor is clicked. Bubbles and is composed,
 * so it can be captured on the parent <code>ui-menu</code> element or any ancestor.
 * @example
 * <ui-menu-link href="#">Home Insurance</ui-menu-link>
 */
@Component({
    tag: 'ui-menu-link',
    styles,
})
export class UIMenuLink extends UIElement {
    @Attribute(Boolean) current: boolean = false;
    @Attribute() href?: string;

    @Child('.ui-menu-link__link a') link!: HTMLLinkElement;

    private observer!: MutationObserver;
    private linkClickHandler?: EventListener;

    /**
     * Fires the shared menu-click event.
     * @fires event:menu-click
     * @private
     */
    private handleLinkClick(event: Event): void {
        if (dispatchMenuClick(this, this.href, event)) {
            event.preventDefault();
        }
    }

    /**
     * Creates the link element wrapper with appropriate attributes.
     * @private
     */
    private createLink() {
        return this.createElement({
            tagName: 'ui-link',
            classList: {
                'ui-menu-link__link': true,
            },
            attributes: {
                'href': this.href,
                'aria-current': this.current ? 'page' : null,
            },
            children: this.detachChildNodes(),
        });
    }

    /**
     * Handles observed attribute mutations by keeping aria-current in sync.
     * @private
     */
    private handleMutations(): void {
        if (this.current) {
            this.link?.setAttribute('aria-current', 'page');
        } else {
            this.link?.removeAttribute('aria-current');
        }
    }

    /**
     * @inheritDoc
     */
    render() {
        if (!this.getAttribute('id')) {
            this.setAttribute('id', Digest.randomId());
        }
        insertElements(this, [this.createLink()]);
    }

    /**
     * @inheritDoc
     */
    hydrate(): void {
        this.observer = new MutationObserver(this.handleMutations.bind(this));
        this.observer.observe(this, {
            attributes: true,
            attributeFilter: ['current'],
        });

        this.linkClickHandler = this.handleLinkClick.bind(this);
        this.link?.addEventListener('click', this.linkClickHandler);
    }

    /**
     * @inheritDoc
     */
    disconnect(): void {
        this.observer.disconnect();
        if (this.linkClickHandler) {
            this.link?.removeEventListener('click', this.linkClickHandler);
        }
    }

    /**
     * @inheritDoc
     */
    reconnect(): void {
        this.observer.observe(this, {
            attributes: true,
            attributeFilter: ['current'],
        });
        if (this.linkClickHandler) {
            this.link?.addEventListener('click', this.linkClickHandler);
        }
    }
}
